import dayjs from 'dayjs';
import {
  ANZAHL_TAGE_TIER_ZU_JUNG,
  isTierZuJungByToday,
  ValueLabelElement,
} from '@ps20-frontend/frontend-models';
import { TiergruppeAnimal } from '@ps20-frontend/shop-integration-redux';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { Selbstbeteiligung, Tiergeschlecht } from '@ps20/bff-models';
import { isListedBreed } from '../models/breeds';
import { breedTypeNeedsDogSize, thhListedBreedCode } from '../models/tier';
import {
  TgvDogSize,
  TgvKatalog,
  TgvTier,
  TgvVorschaeden,
  TgvVorvertrag,
} from '../models/tiergruppe.model';
import { tkvProdukttabelle } from '../models/tkvProdukttabelle';

/**
 * The child apps own their state shape. The mappers only overwrite the fields this form answers
 * and keep everything else the shop already put into the store (Versicherungsnehmer,
 * Vertragsoptionen, role, transient state, ...).
 */
export type ChildState = { versicherungsnehmer?: object } & Record<string, unknown>;

// Same values as AnzahlTiereEnum of the THH apps; the apps must not import each other.
const EIN_TIER = 'EIN_TIER';
const MEHRERE_TIERE = 'MEHRERE_TIERE';
const MINIMUM_MEHRERE_TIERE = 2;

/**
 * Like the TKV reducer on a new birth date: an animal younger than 8 weeks is insured from its
 * 8th week on, every other one from tomorrow.
 */
export const beginnDatumFor = (birthDate: string): string =>
  isTierZuJungByToday(birthDate, DATEFORMAT_LOCAL_STATE)
    ? dayjs(birthDate, DATEFORMAT_LOCAL_STATE)
        .add(ANZAHL_TAGE_TIER_ZU_JUNG, 'day')
        .format(DATEFORMAT_LOCAL_STATE)
    : dayjs().add(1, 'd').format(DATEFORMAT_LOCAL_STATE);

/**
 * A (re)created offer starts like a new one: no product with prices and no pricing result from
 * an earlier run. Otherwise the offer page would price the old product and the reloaded product
 * would stay without prices. The request hashes stay: only the pricing result of the shop uses them.
 */
const withoutPricing = <S extends ChildState>(current: S, emptyProdukt: unknown): S => ({
  ...current,
  produkt: emptyProdukt,
  gesamtbeitrag: 0,
  jahresbeitrag: 0,
  gesamtbeitragMitRabatt: 0,
});

/** Whether the master product (with its Produktlinien) is in the store already. */
export const hasLoadedProdukt = (current: ChildState): boolean =>
  ((current['produkt'] as { produktlinien?: unknown[] } | null)?.produktlinien?.length ?? 0) > 0;

/**
 * A repeated run keeps the product and the contract options as they are, because the user may
 * have changed them on the offer page (Produktlinie, Zusatzoptionen, Selbstbeteiligung, ...),
 * like the product apps keep them when the user goes back to the input page and forward again.
 */
const withoutPrices = <S extends ChildState>(current: S): S => {
  const produkt = current['produkt'] as { produktlinien?: object[] };
  return {
    ...current,
    produkt: {
      ...produkt,
      produktlinien: produkt.produktlinien?.map(linie => ({
        ...linie,
        bruttopreis: undefined as number | undefined,
      })),
    },
    gesamtbeitrag: 0,
  };
};

/**
 * The TKV V2 product depends on the type of the animal: the loaded one is kept as long as the
 * animal of the offer is still of that type.
 */
const birthDateChanged = (current: ChildState, tier: TgvTier): boolean =>
  (current.versicherungsnehmer as { animalBirthDate?: string } | undefined)?.animalBirthDate !==
  tier.birthDate;

// Value of CoverageType.OPSCHUTZ of the TKV V2 app; the apps must not import each other.
const TKV_COVERAGE_OPSCHUTZ = 'opSchutz';

export const keepsTkvV2Produkt = (current: ChildState, tier: TgvTier): boolean =>
  hasLoadedProdukt(current) &&
  (current.versicherungsnehmer as { animalType?: string } | undefined)?.animalType === tier.tierart;

const withPendingTableRetargeted = <S extends ChildState>(state: S, tier: TgvTier): S => {
  const transientState = state['transientState'] as
    { expectedProdukttabelle?: unknown } | null | undefined;
  if (transientState?.expectedProdukttabelle == null) {
    return state;
  }
  return {
    ...state,
    transientState: { ...transientState, expectedProdukttabelle: tkvProdukttabelle(tier.tierart) },
  };
};

/**
 * A THH offer of the group is new business unless it was created from a Vorvertrag: nothing of an
 * earlier Beibehaltung stays then.
 */
const asNeugeschaeft = <S extends ChildState>(current: S): S => {
  const {
    vorvertragsNummern: _vorvertragsNummern,
    beibehalten: _beibehalten,
    ...vertragsoptionen
  } = (current['vertragsoptionen'] ?? {}) as Record<string, unknown>;
  return {
    ...current,
    vertragsoptionen,
    versicherungsnehmer: { ...current.versicherungsnehmer, tiere: [] },
  };
};

export const toTkvV2State = <S extends ChildState>(
  current: S,
  tier: TgvTier,
  katalog: TgvKatalog,
): S => ({
  ...(keepsTkvV2Produkt(current, tier)
    ? withoutPrices(current)
    : withPendingTableRetargeted(
        withoutPricing(current, {
          ...(current['produkt'] as object),
          produktlinien: [],
          uebergreifend: [],
        }),
        tier,
      )),
  versicherungsnehmer: {
    ...current.versicherungsnehmer,
    animalType: tier.tierart,
    animalGender: tier.gender,
    animalName: tier.name.trim(),
    animalBirthDate: tier.birthDate,
    dogBreedType: tier.dogBreedType,
    breed: tier.breed,
    dogMotherBreedType: tier.dogMotherBreedType,
    dogMotherBreed: tier.dogMotherBreed,
    dogSize: breedTypeNeedsDogSize(tier) ? tier.dogSize : null,
    catKeepingType: tier.catKeepingType,
    hasIllnesses: tier.hasIllnesses,
    selectedIllnesses: tier.hasIllnesses ? tier.selectedIllnesses : null,
    dogBreeds: katalog.dogBreeds,
    horseBreeds: katalog.horseBreeds,
    catBreeds: katalog.catBreeds,
    animalIllnesses: katalog.animalIllnesses,
  },
  vertragsoptionen: {
    ...(current['vertragsoptionen'] as object),
    // Like the TKV reducer, which sets it only when the birth date is answered or changed.
    ...(birthDateChanged(current, tier) ? { beginnDatum: beginnDatumFor(tier.birthDate) } : {}),
    // The product that is loaded anew is the OP-Schutz one (the TKV reducer resets the coverage
    // type on a change of the animal type); a kept product keeps the coverage type of the user.
    ...(keepsTkvV2Produkt(current, tier) ? {} : { coverageType: TKV_COVERAGE_OPSCHUTZ }),
  },
});

// Same codes as DOG_SIZE_CODE_MAP of the TKV app: "Großer Hund" / "Kleiner Hund".
const DOG_SIZE_BREED_CODES: Record<TgvDogSize, string> = {
  [TgvDogSize.LARGE]: 'H132',
  [TgvDogSize.SMALL]: 'H133',
};

const thhBreedOf = (dog: TgvTier, thhDogBreeds: ValueLabelElement[]): string | null =>
  thhListedBreedCode(dog, thhDogBreeds) ?? (dog.dogSize ? DOG_SIZE_BREED_CODES[dog.dogSize] : null);

const THH_GENDER_CODES: Record<Tiergeschlecht, string> = {
  [Tiergeschlecht.MAENNLICH]: 'M',
  [Tiergeschlecht.WEIBLICH]: 'W',
};

const isoBirthDateOf = (birthDate: string): string => {
  const date = dayjs(birthDate, DATEFORMAT_LOCAL_STATE, true);
  return date.isValid() ? date.format(DATEFORMAT_SHORT_ISO) : '';
};

const tiergruppeAnimalOf = (animal: TgvTier, breed: string | null): TiergruppeAnimal => ({
  cardId: animal.cardId,
  name: animal.name.trim(),
  gender: THH_GENDER_CODES[animal.gender] ?? '',
  birthDate: isoBirthDateOf(animal.birthDate),
  ...(breed ? { breed } : {}),
});

const thhHorseBreedOf = (horse: TgvTier, thhHorseBreeds: ValueLabelElement[]): string | null => {
  const code = horse.breed?.value;
  return code && isListedBreed(code, thhHorseBreeds) ? code : null;
};

/**
 * The first run starts from new business. A repeated run (the offer was created by the group
 * before) keeps product and contract options, if the product is loaded.
 */
const freshThhState = <S extends ChildState>(
  current: S,
  repeated: boolean,
  vorvertrag: TgvVorvertrag | null,
  defaultSelbstbeteiligung: Selbstbeteiligung,
): S => {
  if (repeated && hasLoadedProdukt(current)) {
    return withoutPrices(current);
  }
  const withoutPrice = withoutPricing(current, null);
  if (vorvertrag) {
    return withoutPrice;
  }
  const neugeschaeft = asNeugeschaeft(withoutPrice);
  return repeated
    ? neugeschaeft
    : {
        ...neugeschaeft,
        vertragsoptionen: {
          ...(neugeschaeft['vertragsoptionen'] as object),
          selbstbeteiligung: defaultSelbstbeteiligung,
        },
      };
};

export const toThhHundState = <S extends ChildState>(
  current: S,
  dogs: TgvTier[],
  vorschaeden: TgvVorschaeden,
  katalog: TgvKatalog,
  repeated = false,
  vorvertrag: TgvVorvertrag | null = null,
): S => {
  const breeds = dogs.map(dog => thhBreedOf(dog, katalog.thhDogBreeds));
  const isMultiple = dogs.length > 1;
  const fresh = freshThhState(
    current,
    repeated,
    vorvertrag,
    Selbstbeteiligung.SELBSTBETEILIGUNG_0300,
  );
  return {
    ...fresh,
    versicherungsnehmer: {
      ...fresh.versicherungsnehmer,
      hunderassenAuswahlElemente: katalog.thhDogBreeds.map(({ value, label }) => ({
        value,
        label,
      })),
      anzahlTiere: isMultiple ? MEHRERE_TIERE : EIN_TIER,
      rasseEinHund: isMultiple ? null : (breeds[0] ?? null),
      anzahlMehrereHunde: isMultiple ? dogs.length : MINIMUM_MEHRERE_TIERE,
      rasseMehrereHunde: isMultiple ? breeds : new Array(MINIMUM_MEHRERE_TIERE),
      tiergruppeAnimals: dogs.map((dog, index) => tiergruppeAnimalOf(dog, breeds[index])),
    },
    vorschaeden: {
      vorhanden: vorschaeden.vorhanden,
      thhHundVorschaeden: vorschaeden.vorhanden ? vorschaeden.anzahl : 0,
    },
  };
};

/**
 * DUMMY until the pricing is connected: the Stockmaß is not asked in the form any more, but the
 * THH-Pferd TAAS request still derives the size of every horse from it.
 */
export const DUMMY_STOCKMASS = 'KLEINER';

export const toThhPferdState = <S extends ChildState>(
  current: S,
  horses: TgvTier[],
  vorschaeden: TgvVorschaeden,
  katalog: TgvKatalog,
  repeated = false,
  vorvertrag: TgvVorvertrag | null = null,
): S => {
  const isMultiple = horses.length > 1;
  const count = isMultiple ? horses.length : MINIMUM_MEHRERE_TIERE;
  const fresh = freshThhState(
    current,
    repeated,
    vorvertrag,
    Selbstbeteiligung.SELBSTBETEILIGUNG_0500,
  );
  return {
    ...fresh,
    versicherungsnehmer: {
      ...fresh.versicherungsnehmer,
      anzahlTiere: isMultiple ? MEHRERE_TIERE : EIN_TIER,
      stockmass: DUMMY_STOCKMASS,
      anzahlMehrerePferde: count,
      stockmassMehrerePferde: new Array(count).fill(DUMMY_STOCKMASS),
      tiergruppeAnimals: horses.map(horse =>
        tiergruppeAnimalOf(horse, thhHorseBreedOf(horse, katalog.thhHorseBreeds)),
      ),
    },
    vorschaeden: {
      vorhanden: vorschaeden.vorhanden,
      thhPferdVorschaeden: vorschaeden.vorhanden ? vorschaeden.anzahl : 0,
    },
  };
};
